// ----- chPopover -------------------------------------------------------------

import angular from 'angular';
import app from 'app';
import Tether from 'tether';

import {
  ARROW_OFFSET_ADJUSTMENT,
  ARROW_TEMPLATE,
  DEFAULT_OPTIONS,
  POPOVER_ACTIVE_CLASS,
  POPOVER_CLASS,
  POPOVER_CLOSE_EVENT,
  POPOVER_TEMPLATE,
  SELF_CLOSING_TAGS,
  SIDES,
  TRIGGERS
} from 'etc/constants';

import {
  applyStyle,
  calculateEdgeOverlap,
  calculateOverlap,
  computeArrowStyle,
  isEdgeOverlapAttachment,
  isPointBoundedByRect,
  kebabString,
  mean,
  head,
  last,
  throttleMemoized,
  cssValue
} from 'lib/utils';


// Install Tether middleware.
Tether.modules.push({
  initialize() {
    this.position();
  },
  position(pos) {
    if (this.options.$onReposition) {
      return this.options.$onReposition(pos);
    }
  }
});


function PopoverCtrl($animate, $compile, $document, $element, $injector, $log, $q, $scope, $templateRequest, $timeout) {
  const Popover = this;


  // ----- Data ----------------------------------------------------------------

  /**
   * Contains combined user and default options for the popover instance.
   *
   * @type {object}
   */
  const options = {};


  /**
   * Basis width for the popover arrow.
   *
   * See utils/cssValue.
   *
   * @type {object}
   */
  let arrowWidth;


  /**
   * Basis height for the popover arrow.
   *
   * See utils/cssValue.
   *
   * @type {object}
   */
  let arrowHeight;


  /**
   * jQuery/jqLite reference to the arrow element.
   *
   * @type {object}
   */
  let arrowElement;


  /**
   * jQuery/jqLite reference to the component element, if a component was used.
   *
   * @type {object}
   */
  let componentElement;


  /**
   * jQuery/jqLite reference to the wrapper element.
   *
   * @type {object}
   */
  let element;


  /**
   * Reference to the Tether instance.
   *
   * @type {object}
   */
  let tether;


  // ----- Private Methods -----------------------------------------------------

  /**
   * Logs a prefixed message to the console if debugging is enabled.
   *
   * @param  {...arglist} args
   */
  const debug = throttleMemoized((...args) => {
    if (options.debug) {
      const prefix = '[chPopover' + (typeof options.debug === 'string' ? ':' + options.debug : '') + ']';
      $log.debug(prefix, ...args);
    }
  }, 1000);


  /**
   * Shows the popover.
   */
  function showPopover() {
    if (!element || !tether) {
      return;
    }

    const animationEl = componentElement ? componentElement.children() : element.children();

    $scope.$applyAsync(() => {
      applyStyle({display: 'block'}, element);

      // We need to reposition the popover twice in order to ensure the arrow is
      // placed correctly; Tether actually supplies two different sets of
      // position descriptors to the reposition handler between these two
      // invocations, and on subsequent invocations the position seems to have
      // "settled". It is not clear why this is the case, but calling position()
      // twice ensures correct behavior.
      tether.position();
      tether.position();

      $animate.addClass(animationEl, POPOVER_ACTIVE_CLASS).then(() => {
        Popover.$isActive = true;
        debug('Popover is active.');
      });
    });
  }


  /**
   * Hides the popover.
   */
  function hidePopover() {
    if (!element) {
      return;
    }

    // Notify child scopes that we are closing.
    $scope.$broadcast(POPOVER_CLOSE_EVENT, Popover);

    const animationEl = componentElement ? componentElement.children() : element.children();

    $scope.$applyAsync(() => {
      $animate.removeClass(animationEl, POPOVER_ACTIVE_CLASS).then(() => {
        applyStyle({display: 'none'}, element);
        Popover.$isActive = false;
        debug('Popover is inactive.');
      });
    });
  }


  /**
   * Fetches either a template string, template URL, or builds a component
   * template, depending on configuration.
   *
   * @return {promise<string>} - Resolves with a template string.
   */
  function getTemplate() {
    const {component, locals, templateUrl, template} = options;

    if (component) {
      // Ensure the component is registered.
      if ($injector.has(component + 'Directive')) {
        // Convert the component's name to kebab case.
        const kebabName = kebabString(component);

        // Create a string of attribute names and values for each property in "locals".
        const attrs = locals ? Object.keys(locals).map(local => {
          return `${kebabString(local)}="locals.${local}"`;
        }).join(' ') : '';

        // Create a template for the component, injecting all locals.
        return $q.when(`<${kebabName} $close="$close" ${attrs}></${kebabName}>`);
      } else {
        // Reject if the component could not be found.
        return $q.reject(new Error(`Unknown component: "${component}".`));
      }
    } else if (templateUrl) {
      return $templateRequest(templateUrl);
    } else if (template) {
      return $q.when(template);
    } else {
      // If no template was provided, reject.
      return $q.reject(new Error('Provide a "component", "templateUrl", or "template" for popovers.'));
    }
  }


  /**
   * Compiles a template using the provided locals.
   *
   * @param  {string} template
   * @return {object} - Contains references to the compiled template element and
   *   wrapper element.
   */
  function compileTemplate(template) {
    return $q(resolve => {
      const {locals} = options;

      return $q[locals && locals.then ? 'when' : 'all'](locals)
      .then(locals => {
        Object.assign($scope, {
          $close: hidePopover,
          locals
        });

        const wrapperEl = angular.element(POPOVER_TEMPLATE);
        const compiledEl = $compile(template)($scope);

        wrapperEl.append(compiledEl);

        if (options.component) {
          // If using a component, wait for its controller to come online before
          // resolving. This ensures the component's template will be fully
          // loaded before we try to append the arrow element to it.
          const cancelWatcher = $scope.$watch(() => {
            return compiledEl.controller(options.component);
          }, controller => {
            if (controller) {
              cancelWatcher();
              resolve({compiledEl, wrapperEl});
            }
          });
        } else {
          // Otherwise, wait for the element's ready event to fire.
          compiledEl.ready(() => {
            resolve({compiledEl, wrapperEl});
          });
        }
      });
    });
  }


  /**
   * Invoked whenever the Tether is repositioned, this function is responsible
   * for recalculating the arrow position, or hiding it if it cannot be
   * displayed.
   *
   * In order for the arrow to be displayed, the following must be true:
   *
   * 1. There must be an overlap between two complementary edges of the popover
   *    and reference element. Ex: top/bottom, left/right.
   *
   * 2. The location of this overlap on the popover element's edge must be such
   *    that if the arrow were placed at the overlap midpont, it would not hang
   *    off the edge of the popover. Ex: For an arrow with a base of 24px, the
   *    overlap midpoint must be at least 12px from the corner of the popover.
   *
   * See: http://tether.io/advanced/extending_tether/
   *
   * @param  {object} pos - Tether position object, passed in from Tether.
   * @return {object} - Modified position object, if applicable.
   */
  function onReposition(pos) {
    const {edge, elSide, overlap} = Object.assign({}, calculateEdgeOverlap(pos.elementPos, pos.targetPos));


    // If the configured attachment(s) prevent ovrlap, or if no overlapping edge
    // was found (due to offsets), or if the consumer has not specified a
    // non-zero value for the arrow, hide the arrow and return.
    if (!isEdgeOverlapAttachment(options) || !edge || !arrowWidth) {
      applyStyle({
        display: 'none'
      }, arrowElement);

      return;
    }


    // Get the top/left axes to use for applying offsets to the arrow and popover
    // based on the side the arrow is on.
    const [, offsetAxis, popoverOffsetAxis] = SIDES[edge];


    // Compute the halfway point of the edge overlap in terms of absolute pixels
    // from the start of the element's overlapping side.
    const overlapMidpoint = cssValue(mean(...overlap)).add('px');


    // Compute the segment of the element's edge, in terms of absolute pixels
    // from top/left that would overlap with the arrow base if the arrow were to
    // be centered about the midpoint between the element and reference element
    // edge overlap.
    const arrowBaseRange = [
      cssValue(pos.elementPos[offsetAxis] + overlapMidpoint).subtract(arrowWidth.divide(2)),
      cssValue(pos.elementPos[offsetAxis] + overlapMidpoint).add(arrowWidth.divide(2))
    ];


    // Calculate the overlap between the edge range and the arrow base range.
    const arrowBaseOverlap = calculateOverlap(arrowBaseRange, elSide);


    // If the computed overlap values are not exactly the same as the projected
    // values, it means the arrow would overflow/hang off the edge of the
    // popover if it were rendered.
    if (head(arrowBaseRange).eq(head(arrowBaseOverlap)) && last(arrowBaseRange).eq(last(arrowBaseOverlap))) {

      // Ensure the arrow is visible and apply the required styles to render it
      // based on the current side and desired arrow dimensions.
      applyStyle(Object.assign(computeArrowStyle(edge, arrowWidth, arrowHeight), {
        display: 'block'
      }), arrowElement);


      // Set the arrow's offset along the correct axis to the overlap midpont.
      // If the arrow is on the left/right of the element, this will be "top".
      // If it is on the top/bottom of the element, it will be "left".
      debug(`Offsetting arrow ${cssValue(overlapMidpoint).value()} from the ${offsetAxis}.`);

      applyStyle({
        [offsetAxis]: overlapMidpoint.value()
      }, arrowElement);


      // Adjust the position of the popover along the correct axis so that it
      // is far enough away from the reference element to fit the arrow. If
      // the popover is above/below the reference element, this will be "top".
      // If it is to the left/right of the element, it will be "left". If it
      // is to the right of or below the reference element, the offset should
      // be negated.
      const offsetModifier = arrowHeight.subtract(ARROW_OFFSET_ADJUSTMENT)
        .multiply(edge === 'right' || edge === 'bottom' ? -1 : 1);


      // Return the updated position object, having modified the top or left
      // offset by the correct amount.
      debug(`Adding ${offsetModifier.value()} of ${popoverOffsetAxis} offset to popover for arrow.`);

      return Object.assign(pos, {
        [popoverOffsetAxis]: cssValue(pos[popoverOffsetAxis]).add(offsetModifier)
      });
    } else {

      // There is no overlap, or the overlap is insufficient to fit the arrow,
      // so hide it.
      debug([
        `Insufficient space on ${edge} edge of popover to place an arrow of`,
        `width ${arrowWidth.value()}. Consider applying offsets or reducing arrow size.`
      ].join(' '));

      applyStyle({display: 'none'}, arrowElement);
    }
  }


  /**
   * Attaches event listeners, creates a Tether instance, and adds the popover
   * element into the DOM.
   */
  function configurePopover() {
    if (!element || !arrowElement) {
      debug('Element/arrow element not ready.');
      return;
    }

    // [1] Prepare elements.
    applyStyle({position: 'absolute'}, element);
    applyStyle({position: 'absolute'}, arrowElement);


    // [2] Add the popover base class to the correct element(s).
    if (componentElement) {
      // If using a component, add the class to each top level element of the
      // component's template, skipping the component wrapper element.
      componentElement.children().addClass(POPOVER_CLASS);
    } else {
      // If using a template, add the class to each top-level element of the
      // template.
      element.children().addClass(POPOVER_CLASS);
    }


    // [3] Insert the popover as a child of the reference element.
    $element.append(element);


    // [4] Get basis arrow dimensions.
    const {width, height} = getComputedStyle(arrowElement[0]);
    arrowWidth = cssValue(width);
    arrowHeight = cssValue(height).add(ARROW_OFFSET_ADJUSTMENT);


    element.ready(() => {
      // [5] Create a new Tether instance.
      tether = new Tether({
        $onReposition: onReposition,
        element: element[0],
        target: $element[0],
        attachment: String(options.attachment),
        targetAttachment: String(options.targetAttachment),
        offset: String(options.offset),
        targetOffset: String(options.targetOffset),
        constraints: options.constraints
      });

      applyStyle({display: 'none'}, element);
    });
  }


  /**
   * Accepts a point object and either a DOM element or jQlite element wrapper
   * and retruns true if the point is bounded by the element.
   *
   * @param  {object}  point - Object that should contain 'x' and 'y' values.
   * @param  {object}  subjectElement - Raw DOM element or jQlite element.
   * @return {boolean}
   */
  function isElementHovered(point, subjectElement) {
    // Unwrap jQlite elements.
    const el = subjectElement[0] ? subjectElement[0] : subjectElement;

    // Get the element's bounding box.
    const rect = el.getBoundingClientRect();

    // Adjust bounding box for page scroll offsets and determine if it bounds
    // the provided point.
    return isPointBoundedByRect(point, {
      top: rect.top + window.pageYOffset,
      height: rect.height,
      left: rect.left + window.pageXOffset,
      width: rect.width
    });
  }


  /**
   * Encapsulates logic which handles "hide" events to avoid duplication.
   *
   * @param  {object} hideEvent - Event.
   */
  function handleHideEvent(hideEvent) {
    if (!Popover.$isActive) {
      return;
    }

    // Prevent the event from bubbling.
    hideEvent.stopPropagation();

    // Capture the mouse position at the time the hide event fired.
    const mousePosition = {
      x: hideEvent.pageX,
      y: hideEvent.pageY
    };

    const onMouseMove = e => {
      mousePosition.x = e.pageX;
      mousePosition.y = e.pageY;
    };

    // If the consumer specified a non-zero hide grace period, update the mouse
    // position if it moves during the grace period.
    if (options.hideGracePeriod > 0) {
      $document.on('mousemove', onMouseMove);
    }

    debug(`Got hide event "${hideEvent.type}"${options.hideGracePeriod ?
      `, but will wait ${options.hideGracePeriod}ms.`
      : '.'}`
    );

    $timeout(() => {
      // Perform another "isActive" check here, as something may have changed
      // during the grace period.
      if (!Popover.$isActive) {
        return;
      }

      // Unregister event listener.
      $document.off('mousemove', onMouseMove);

      // Determine if the mouse is still hovering over the popover element.
      const isPopoverHovered = isElementHovered(mousePosition, element);

      let isTriggerHovered = false;

      // If the event involved mouse movement, we can safely assume that the
      // consumer will want to "negate" closing the popover if the mouse is
      // still in the trigger element or re-enters the trigger element when the
      // grace period expires.
      if (/mouse(move|leave|out)/g.test(hideEvent.type)) {
        debug('Event involved mouse motion. Will perform trigger hover check.');
        isTriggerHovered = isElementHovered(mousePosition, $element);
      }

      if (isPopoverHovered) {
        debug('Not hiding popover because it is still being hovered.');
        return;
      }

      if (isTriggerHovered) {
        debug('Not hiding popover because the trigger element is still being hovered.');
        return;
      }

      debug('Hiding popover.');
      hidePopover();
    }, options.hideGracePeriod);
  }


  /**
   * Sets up event listeners on the reference element based on the configured
   * trigger.
   */
  function configureEventListeners() {
    // Ensure trigger(s) are an array, regardless of whether the user passed a
    // string or an array.
    [].concat(options.trigger).forEach(trigger => {
      if (!TRIGGERS[trigger]) {
        throw new Error(`Unknown trigger: "${trigger}"`);
      }

      // Register listeners for each DOM event associated with the trigger's
      // show events.
      TRIGGERS[trigger].show.forEach(showEvent => {
        debug(`Configuring show event "${showEvent}".`);
        // Indicate that the reference element is clickable when using the
        // "click" trigger.
        if (showEvent === 'click') {
          applyStyle({cursor: 'pointer'}, $element);
        }

        // Register show events on the reference element.
        $element.on(showEvent, () => {
          // If the popover is already active, bail.
          if (Popover.$isActive) {
            return;
          }

          debug(`Got show event "${showEvent}".`);
          showPopover();
        });
      });

      // Register listeners for each DOM event associated with the trigger.
      TRIGGERS[trigger].hide.forEach(hideEvent => {
        debug(`Configuring hide event "${hideEvent}".`);

        // Register hide event on the reference element.
        $element.on(hideEvent, handleHideEvent);

        // Register hide event on the popover element.
        element.on(hideEvent, handleHideEvent);

        // (Optionally) register hide event on the document.
        if (options.documentCloseEvents) {
          debug(`Adding "${hideEvent}" listener to document.`);
          $document.on(hideEvent, handleHideEvent);
        }
      });

      // If a parent scope broadcats a close event, this popover should close too.
      $scope.$on(POPOVER_CLOSE_EVENT, (event, fromPopover) => {
        if (Popover !== fromPopover) {
          hidePopover();
        }
      });
    });
  }


  // ----- Public Methods ------------------------------------------------------

  Popover.$onInit = () => {
    if (SELF_CLOSING_TAGS.includes($element[0].tagName)) {
      throw new Error([
        `Self-closing tag "${$element[0].tagName.toLowerCase()}"`,
        'cannot be used as a popover target.'
      ].join(' '));
    }

    // Build options.
    Object.assign(options, DEFAULT_OPTIONS, Popover.options);

    // Warn if attachments have been used that will never result in edge overlap.
    if (!isEdgeOverlapAttachment(options)) {
      debug([
        `Warning: Attachment "${options.attachment}"`,
        `and targetAttachment "${options.targetAttachment}"`,
        'does not guarantee edge overlap.'
      ].join(''));
    }

    getTemplate()
    .then(compileTemplate)
    .then(elements => {
      // Wrapper is the <aside> that contains the rest of the popover template
      // and arrow. Compiled is a reference to the element compiled using the
      // popover template.
      const {wrapperEl, compiledEl} = elements;

      // Set element references and append arrow element to popover element.
      element = wrapperEl;
      arrowElement = angular.element(ARROW_TEMPLATE);

      if (options.component) {
        // If using a component, save a reference to the component's element
        // and append the arrow to the first child of the component's template.
        componentElement = angular.element(compiledEl);
        angular.element(componentElement.children()[0]).append(arrowElement);
      } else {
        // If using a basic template, append the arrow element to the first child
        // of the template.
        angular.element(compiledEl[0]).append(angular.element(arrowElement));
      }

      configurePopover();
      configureEventListeners();

      debug('Configured popover:', Popover);
    })
    .catch(err => {
      debug('Invalid configuration on element', $element, ':', err.message);
      throw err;
    });
  };


  Popover.$onDestroy = () => {
    if (tether) {
      tether.destroy();
    }

    if (element) {
      element.remove();
    }
  };


  // Perform tear-down when our scope is destroyed.
  $scope.$on('$destroy', Popover.$onDestroy);
}


app.directive('chPopover', () => ({
  restrict: 'A',
  scope: true,
  bindToController: {
    options: '<chPopover'
  },
  controller: PopoverCtrl,
  controllerAs: '$chPopover'
}));



// WEBPACK FOOTER //
// ./directives/chPopover.js